import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Card, CardContent, Chip, Divider, Skeleton, Typography } from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import { useNavigate, useParams } from "react-router-dom";
import Banner1 from "../assets/Banner_1.jpeg";
import Banner2 from "../assets/Banner_2.jpeg";

import DashboardLayout from "../layout/DashboardLayout";
import { getEventById } from "../data/upcomingEventsData";

const BACKEND_URL = process.env.REACT_APP_API_BASE_URL;

function getToken() {
  return localStorage.getItem("nb_token") || "";
}


function fmtDate(dateLike) {
  if (!dateLike) return "";
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

function fmtTimeRange(dateLike, endLike) {
  const start = dateLike ? new Date(dateLike) : null;
  const end = endLike ? new Date(endLike) : null;

  const st =
    start && !Number.isNaN(start.getTime())
      ? start.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
      : "";

  const et =
    end && !Number.isNaN(end.getTime())
      ? end.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
      : "";

  if (st && et) return `${st} - ${et}`;
  return st || et || "";
}

const mockBanners = [
  { id: "b1", title: "Art Classes", image: Banner1 },
  { id: "b2", title: "Weekend Events", image: Banner2 },
//  { id: "b3", title: "Creative Workshops", image: BannerMain }
];

export default function EventDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);

  const [checkingBooking, setCheckingBooking] = useState(true);
  const [registering, setRegistering] = useState(false);

  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      setEvent(getEventById(id));
      setLoading(false);
    }, 200);
  }, [id]);

  async function fetchBookingStatus() {
    const token = getToken();
    if (!token || !BACKEND_URL) {
      setCheckingBooking(false);
      return;
    }

    try {
      setCheckingBooking(true);

      const res = await fetch(`${BACKEND_URL}/events/${id}/booking`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });

      const data = await res.json();

      if (res.ok && data?.registered) {
        setAlreadyRegistered(true);
        setBooking(data.booking || null);
      } else {
        setAlreadyRegistered(false);
        setBooking(null);
      }
    } catch (e) {
      // ignore
    } finally {
      setCheckingBooking(false);
    }
  }

  useEffect(() => {
    fetchBookingStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function handleRegister() {
    if (!event) return;

    const token = getToken();
    if (!token) {
      alert("Please login again.");
      return;
    }

    if (!BACKEND_URL) {
      alert("Backend URL not configured. Set REACT_APP_API_URL_LOCAL.");
      return;
    }

    try {
      setRegistering(true);

      const payload = {
        eventSnapshot: {
          title: event.title,
          category: event.category,
          dateTime: event.dateTime,
          endDateTime: event.endDateTime,
          location: event.location,
          coverImage: event.coverImage,
          priceLabel: event.priceLabel
        }
      };

      const res = await fetch(`${BACKEND_URL}/events/${id}/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data?.message || "Registration failed");
        return;
      }

      setAlreadyRegistered(true);
      setBooking(data.booking || null);
    } catch (e) {
      alert("Registration failed");
    } finally {
      setRegistering(false);
    }
  }

  const cover = useMemo(() => {
    return (
      event?.coverImage ||
      "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1600&q=70"
    );
  }, [event]);

  const timeText = fmtTimeRange(event?.dateTime, event?.endDateTime);

  const facilitator = event?.facilitator || {};
  const expList = Array.isArray(event?.whatYouWillExperience) ? event.whatYouWillExperience : [];

  return (
    <DashboardLayout title="Dashboard">
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 320px" }, gap: 3 }}>
        {/* LEFT */}
        <Box sx={{ minWidth: 0 }}>
          {/* Banner */}
          <Card sx={{ overflow: "hidden" }}>
            <Box
              sx={{
                height: 260,
                backgroundImage: `url(${cover})`,
                backgroundSize: "cover",
                backgroundPosition: "center"
              }}
            />
          </Card>

          {/* Header */}
          <Card sx={{ mt: 2 }}>
            <CardContent sx={{ py: 2.25 }}>
              {loading ? (
                <>
                  <Skeleton width="40%" />
                  <Skeleton width="70%" height={32} />
                  <Skeleton width="55%" />
                </>
              ) : !event ? (
                <>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 900 }}>Event not found</Typography>
                  <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate("/events")}>
                    Back to Events
                  </Button>
                </>
              ) : (
                <>
                  <Chip size="small" label={event.category} />

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 320px" },
                      gap: 2.5,
                      mt: 1.2,
                      alignItems: "start"
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: 20, fontWeight: 900, lineHeight: 1.2 }}>
                        {event.title}
                      </Typography>

                      <Typography sx={{ mt: 1, fontSize: 12.8, color: "text.secondary", lineHeight: 1.7 }}>
                        {event.shortDescription}
                      </Typography>

                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1.6 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                          <CalendarMonthOutlinedIcon fontSize="small" sx={{ color: "text.secondary", opacity: 0.85 }} />
                          <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                            {fmtDate(event.dateTime)}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                          <AccessTimeOutlinedIcon fontSize="small" sx={{ color: "text.secondary", opacity: 0.85 }} />
                          <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>{timeText}</Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                          <LocationOnOutlinedIcon fontSize="small" sx={{ color: "text.secondary", opacity: 0.85 }} />
                          <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>{event.location}</Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                          <PeopleAltOutlinedIcon fontSize="small" sx={{ color: "text.secondary", opacity: 0.85 }} />
                          <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                            {event.capacity} participants
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Register panel */}
                    <Card sx={{ border: "1px solid", borderColor: "divider" }}>
                      <CardContent sx={{ py: 2 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: "primary.main" }}>
                          {event.priceLabel}
                        </Typography>

                        <Typography sx={{ mt: 0.4, fontSize: 12.3, color: "text.secondary" }}>
                          {event.seatsRemaining} seats remaining
                        </Typography>

                        <Button
                          fullWidth
                          variant="contained"
                          sx={{
                            mt: 1.6,
                            py: 1.1,
                            ...(alreadyRegistered ? { bgcolor: "success.main", "&:hover": { bgcolor: "success.main" } } : {})
                          }}
                          disabled={checkingBooking || registering || alreadyRegistered}
                          onClick={handleRegister}
                        >
                          {alreadyRegistered ? "Already Registered" : registering ? "Registering..." : "Register for this event"}
                        </Button>

                        <Typography sx={{ mt: 1.1, fontSize: 12.2, color: "text.secondary" }}>
                          Counts towards your participation milestones
                        </Typography>

                        {alreadyRegistered && booking?.id && (
                          <Typography sx={{ mt: 1, fontSize: 12, color: "text.secondary" }}>
                            Booking ID: <b>{booking.id}</b>
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>

          {/* About */}
          {!loading && event && (
            <Card sx={{ mt: 2 }}>
              <CardContent sx={{ py: 2.25 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 900 }}>About the Event</Typography>

                <Typography sx={{ mt: 1.2, fontSize: 12.8, color: "text.secondary", lineHeight: 1.75 }}>
                  {event.description}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography sx={{ fontSize: 13.5, fontWeight: 900 }}>What You’ll Experience</Typography>

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.6, mt: 1.4 }}>
                  {expList.map((b) => (
                    <Box key={b} sx={{ display: "flex", gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: "primary.main", mt: 0.8 }} />
                      <Typography sx={{ fontSize: 12.8, color: "text.secondary" }}>{b}</Typography>
                    </Box>
                  ))}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography sx={{ fontSize: 13.5, fontWeight: 900 }}>Workshop Facilitator</Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1.4, mt: 1.4 }}>
                  <Box
                    sx={{
                      width: 54,
                      height: 54,
                      borderRadius: "50%",
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundImage: `url(${facilitator.avatar})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center"
                    }}
                  />

                  <Box>
                    <Typography sx={{ fontSize: 13.2, fontWeight: 900 }}>{facilitator.name}</Typography>
                    <Typography sx={{ fontSize: 12.3, color: "text.secondary", mt: 0.3 }}>{facilitator.role}</Typography>
                    <Typography sx={{ fontSize: 12.3, color: "text.secondary", mt: 0.2 }}>{facilitator.experience}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>

{/* RIGHT banners */}
<Box
  sx={{
    display: { xs: "none", lg: "flex" },
    flexDirection: "column",
    gap: 2
  }}
>
  {mockBanners.map((b) => (
    <Card
      key={b.id}
      elevation={0}
      sx={{
        overflow: "hidden",
        bgcolor: "background.paper"
      }}
    >
      <Box
        sx={{
          p: 1.5,
          bgcolor: "action.hover"
        }}
      >
        <Box
          sx={{
            height: 320,
            borderRadius: 0,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            backgroundImage: `url(${b.image})`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "contain"
          }}
        />
      </Box>

      <Box sx={{ p: 1.5, pt: 1.2 }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => navigate("/events")}
          sx={{
            borderColor: "primary.main",
            color: "primary.main",
            fontWeight: 800,
            borderRadius: 14,
            py: 1
          }}
        >
          Learn More
        </Button>
      </Box>
    </Card>
  ))}
</Box>


      </Box>
    </DashboardLayout>
  );
}
